import React, { useState } from 'react';
import { Check, Sparkles, Zap, Crown, Gift, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import SubscriptionDialog from '@/components/Subscription/SubscriptionDialog';
import { useNavigate } from 'react-router-dom';

const Pricing = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);

  const plans = [
    {
      name: 'Free',
      price: 0,
      credits: 40,
      icon: Gift,
      color: 'from-gray-500 to-gray-600',
      features: [],
    },
    {
      name: 'Basic',
      price: 8.99,
      credits: 1000,
      icon: Sparkles,
      color: 'from-blue-500 to-cyan-500',
      features: [],
      popular: false,
    },
    {
      name: 'Pro',
      price: 24.99,
      credits: 3000,
      icon: Zap,
      color: 'from-cyan-500 to-turquoise-500',
      features: [],
      popular: true,
    },
    {
      name: 'Elite',
      price: 64.99,
      credits: 8000,
      icon: Crown,
      color: 'from-yellow-500 to-orange-500',
      features: [],
    },
  ];

  const faqs = [
    {
      question: 'What happens to unused credits?',
      answer: 'Credits do not roll over to the next month. We recommend using them before your billing cycle resets.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes! You can cancel your subscription at any time. You\'ll retain access until the end of your billing period.'
    },
    {
      question: 'What\'s the difference between personal and commercial use?',
      answer: 'Personal use is for non-commercial projects. Commercial use (Elite plan) allows you to sell designs or use them in business.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'Due to the digital nature of credits, we generally don\'t offer refunds. However, we review cases with service issues individually.'
    }
  ];

  const handleSelectPlan = (plan) => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    if (plan.name === 'Free') return;
    setSelectedPlan(plan);
    setShowSubscriptionDialog(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 py-12 px-4 gradient-mesh">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-100 to-turquoise-100 rounded-2xl mb-4">
            <Sparkles className="w-8 h-8 text-cyan-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your <span className="bg-gradient-to-r from-cyan-600 to-turquoise-600 bg-clip-text text-transparent">Perfect Plan</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Start creating amazing AI-powered T-shirt designs today. Upgrade or downgrade anytime.
          </p>
        </div>

        {/* Credit Usage Info */}
        <div className="mb-12 max-w-3xl mx-auto">
          <div className="glass rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">How Credits Work</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg">
                <Sparkles className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">New Design</p>
                  <p className="text-sm text-gray-600">36 credits per generation</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-turquoise-50 to-teal-50 rounded-lg">
                <Zap className="w-8 h-8 text-turquoise-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Edit/Regenerate</p>
                  <p className="text-sm text-gray-600">19 credits per edit</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <div
                key={plan.name}
                className={`relative glass rounded-2xl shadow-xl p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl border-0 ${
                  plan.popular ? 'ring-2 ring-cyan-500 shadow-2xl' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-cyan-600 to-turquoise-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${plan.color} rounded-2xl mb-4 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-extrabold bg-gradient-to-r from-cyan-600 to-turquoise-600 bg-clip-text text-transparent">${plan.price}</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                  <div className="mt-2 inline-flex items-center px-3 py-1 bg-gradient-to-r from-cyan-50 to-turquoise-50 rounded-full">
                    <span className="text-sm font-semibold text-cyan-700">{plan.credits} credits/month</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={plan.name === 'Free'}
                  className={`w-full shadow-lg ${
                    plan.popular
                      ? 'bg-gradient-to-r from-cyan-600 to-turquoise-600 hover:from-cyan-700 hover:to-turquoise-700'
                      : plan.name === 'Free'
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gray-800 hover:bg-gray-900'
                  }`}
                >
                  {plan.name === 'Free' ? 'Current Plan' : 'Choose Plan'}
                </Button>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mb-16 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <HelpCircle className="w-12 h-12 text-cyan-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Frequently Asked Questions</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div key={index} className="glass rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border-0">
                <h3 className="font-bold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-cyan-600 to-turquoise-600 rounded-2xl p-8 text-center text-white shadow-2xl">
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
            Our team is here to help you choose the right plan and answer any questions about pricing.
          </p>
          <Button 
            size="lg" 
            variant="secondary" 
            className="bg-white text-cyan-600 hover:bg-gray-100 shadow-lg"
            onClick={() => navigate('/support')}
          >
            Contact Support
          </Button>
        </div>
      </div>

      {showSubscriptionDialog && (
        <SubscriptionDialog
          open={showSubscriptionDialog}
          onClose={() => setShowSubscriptionDialog(false)}
          selectedPlan={selectedPlan}
        />
      )}
    </div>
  );
};

export default Pricing;
